import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, CalendarDays, Video, Map, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'booked', icon: CalendarDays, title: 'Book your strategy session', desc: 'Pick a time and answer a few quick intake questions so David can prepare for you.', to: '/portal/book', cta: 'Book now' },
  { key: 'attended', icon: Video, title: 'Meet with David', desc: 'Join your call. Your Zoom link and details live in My Sessions.', to: '/portal/sessions', cta: 'View sessions' },
  { key: 'roadmap', icon: Map, title: 'Get your strategy roadmap', desc: 'David shares your next-level plan and action items right here in your portal.', to: '/portal/goals', cta: 'See my plan' },
];

export const useJourney = () => {
  const [j, setJ] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/portal/journey`, { withCredentials: true }).then((r) => setJ(r.data)).catch(() => setJ(null));
  }, []);
  return j;
};

export default function JourneyCard({ journey }) {
  if (!journey || journey.progress >= 100) return null;
  const current = STEPS.findIndex((s) => !journey[s.key]);
  return (
    <section className="p-card rounded-2xl p-6 mb-8" data-testid="portal-journey">
      <div className="flex items-end justify-between gap-4 mb-1">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: 'var(--p-faint)' }}>Your path with David</div>
          <h2 className="text-lg font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>3 steps to your next level</h2>
        </div>
        <div className="text-2xl font-bold text-[#1D4ED8]" data-testid="portal-journey-progress">{journey.progress}%</div>
      </div>
      <p className="text-xs mb-5" style={{ color: 'var(--p-faint)' }}>Coaching tools like the Clarity Hub unlock separately as part of your coaching plan.</p>
      <ol className="grid md:grid-cols-3 gap-3">
        {STEPS.map((s, i) => {
          const done = journey[s.key];
          const now = i === current;
          return (
            <li key={s.key} data-testid={`journey-step-${s.key}`}
              className={`rounded-xl border p-4 flex flex-col transition-colors ${now ? 'border-[#1D4ED8] ring-2 ring-[#1D4ED8]/15' : ''}`}
              style={{ background: now ? 'var(--p-surface)' : 'var(--p-surface-2)', borderColor: now ? undefined : 'var(--p-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                {done ? <CheckCircle2 size={18} className="text-emerald-500" />
                  : <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${now ? 'bg-[#1D4ED8] text-white' : 'bg-slate-200 text-slate-500'}`}>{i + 1}</span>}
                <span className="text-sm font-semibold" style={done ? { color: 'var(--p-faint)', textDecoration: 'line-through' } : {}}>{s.title}</span>
              </div>
              <p className="text-xs leading-relaxed flex-1" style={{ color: 'var(--p-muted)' }}>{s.desc}</p>
              {now && (
                <Link to={s.to} data-testid={`journey-cta-${s.key}`}
                  className="mt-3 self-start inline-flex items-center gap-1.5 bg-[#1D4ED8] hover:bg-[#2563EB] text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition-colors">
                  {s.cta} <ArrowRight size={12} />
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
